import { cn } from '@/lib/utils';
import type { DirectorMessage as DirectorMessageType } from '@/hooks/useDirector';
import { ToolExecutionCard } from './ToolExecutionCard';
import { User, Sparkles, AlertCircle } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────

interface DirectorMessageProps {
  message: DirectorMessageType;
  className?: string;
}

// ─── Markdown-like Text Rendering ─────────────────────────────────

function renderMessageContent(content: string): React.ReactNode {
  if (!content) return null;

  // Simple markdown-like rendering for common patterns
  const lines = content.split('\n');

  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        // Empty line
        if (!line.trim()) {
          return <div key={i} className="h-2" />;
        }

        // Code block (inline)
        if (line.includes('`')) {
          const parts = line.split(/(`[^`]+`)/g);
          return (
            <p key={i} className="text-sm">
              {parts.map((part, j) => {
                if (part.startsWith('`') && part.endsWith('`')) {
                  return (
                    <code
                      key={j}
                      className="px-1.5 py-0.5 rounded bg-neutral-1 font-mono text-xs text-neutral-10 shadow-[inset_0_0_0_1px_var(--neutral-4)]"
                    >
                      {part.slice(1, -1)}
                    </code>
                  );
                }
                return <span key={j}>{part}</span>;
              })}
            </p>
          );
        }

        // Bullet points
        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          return (
            <div key={i} className="flex items-start gap-2 text-sm">
              <span className="text-neutral-7 mt-1.5">•</span>
              <span>{line.trim().slice(2)}</span>
            </div>
          );
        }

        // Numbered lists
        const numberedMatch = line.trim().match(/^(\d+)\.\s(.+)/);
        if (numberedMatch) {
          return (
            <div key={i} className="flex items-start gap-2 text-sm">
              <span className="text-neutral-7 font-mono text-xs w-4 text-right">
                {numberedMatch[1]}.
              </span>
              <span>{numberedMatch[2]}</span>
            </div>
          );
        }

        // Bold text
        if (line.includes('**')) {
          const parts = line.split(/(\*\*[^*]+\*\*)/g);
          return (
            <p key={i} className="text-sm">
              {parts.map((part, j) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                  return (
                    <strong key={j} className="font-semibold">
                      {part.slice(2, -2)}
                    </strong>
                  );
                }
                return <span key={j}>{part}</span>;
              })}
            </p>
          );
        }

        // Regular paragraph
        return (
          <p key={i} className="text-sm">
            {line}
          </p>
        );
      })}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────

export function DirectorMessage({ message, className }: DirectorMessageProps) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';
  const isToolMessage = message.toolUse && !message.content;

  // Tool execution card (no text content, just tool use/result)
  if (isToolMessage && message.toolUse) {
    return (
      <div className={cn('px-4 py-2', className)}>
        <ToolExecutionCard
          toolUse={message.toolUse}
          toolResult={message.toolResult}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'px-4 py-3',
        isUser && 'bg-neutral-3',
        isAssistant && 'bg-neutral-2',
        className
      )}
    >
      <div className="flex items-start gap-3 max-w-3xl">
        {/* Avatar */}
        <div
          className={cn(
            'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
            isUser && 'bg-neutral-1 text-neutral-10',
            isAssistant && 'bg-neutral-3 text-neutral-10',
            isSystem && 'bg-warning-transparent text-warning'
          )}
        >
          {isUser && <User className="w-4 h-4" />}
          {isAssistant && <Sparkles className="w-4 h-4" />}
          {isSystem && <AlertCircle className="w-4 h-4" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Role label */}
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                'text-xs font-medium',
                isUser && 'text-neutral-10',
                isAssistant && 'text-neutral-10',
                isSystem && 'text-warning'
              )}
            >
              {isUser ? 'You' : isAssistant ? 'Director' : 'System'}
            </span>
            {message.isStreaming && (
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-neutral-7 motion-safe:animate-pulse" />
                <span className="text-xs text-neutral-7">typing</span>
              </span>
            )}
          </div>

          {/* Message content */}
          <div
            className={cn(
              'text-neutral-10',
              isSystem && 'text-warning'
            )}
          >
            {renderMessageContent(message.content)}
          </div>

          {/* Inline tool use (if both content and tool use exist) */}
          {message.toolUse && message.content && (
            <div className="mt-3">
              <ToolExecutionCard
                toolUse={message.toolUse}
                toolResult={message.toolResult}
              />
            </div>
          )}

          {/* Streaming cursor */}
          {message.isStreaming && (
            <span className="inline-block w-2 h-4 bg-neutral-10 motion-safe:animate-pulse ml-1 align-middle" />
          )}
        </div>
      </div>
    </div>
  );
}

export default DirectorMessage;
